import {
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { SIGNIN_COLORS, SIGNIN_MONO, SIGNIN_THEME, type SignInAudience } from './signInTheme.ts';

/**
 * Form controls for the sign-in screens only.
 *
 * WHY THESE EXIST RATHER THAN OVERRIDES ON <TextInput>/<Btn>. The shared
 * controls are tokenised — `bg-[var(--ui-card-bg,#ffffff)]`,
 * `border-[var(--ui-control-border,#e2e8f0)]`,
 * `rounded-[var(--ui-control-radius,0.375rem)]`. Restyling them from a call site
 * means putting `bg-[#FFFDF7]` and `rounded-[12px]` up against arbitrary
 * utilities for the SAME property, where the winner is decided by the order
 * Tailwind happens to emit them and nothing guarantees which that is —
 * Field.tsx's own comment calls this out as the reason its invalid variant is a
 * whole separate string rather than an appended `border-red-400`.
 *
 * So the sign-in look is built from its own base instead. The shared controls
 * are left exactly as they are for every other screen in three apps, and there
 * is no class-order coin-flip on the one page every user has to get through.
 *
 * SIZING IS AUDIENCE-DRIVEN, not breakpoint-driven. The canvas gives the driver
 * 58px controls at every width and the two desk audiences 50px, because the
 * driver portal is a phone product on a phone in a yard — not because a driver
 * is on a narrow screen. Both clear the 44px touch minimum and the 16px font
 * floor below which iOS Safari zooms the viewport on focus.
 */

/**
 * Who a control is sized for. The three sign-in audiences, plus the public
 * visitor kiosk, whose "Visitor Sign-in" canvas draws the driver's large
 * controls for the driver's reason: it is a phone (or a reception tablet) in
 * someone's hand, not a desk. Kept OUT of `SignInAudience` because that type
 * also keys `SIGNIN_PANELS`, and the visitor screens never render a panel.
 */
export type SignInControlAudience = SignInAudience | 'visitor';

/** Control metrics per audience, from the canvas. */
function metrics(audience: SignInControlAudience) {
  const driver = audience === 'driver' || audience === 'visitor';
  return {
    height: driver ? 58 : 50,
    radius: driver ? 12 : 10,
    fontSize: driver ? 17 : 15,
    // The button runs a point smaller than its field at both sizes — a button
    // label is short and centred, so it does not need the field's reading size.
    buttonFontSize: driver ? 16 : 15,
    labelSize: driver ? 14 : 13,
    labelGap: driver ? 8 : 7,
    padX: driver ? 16 : 15,
  };
}

/** A labelled field. `<label>` wraps the control, so the label is the hit area. */
export function SignInField({
  label,
  audience,
  action,
  hint,
  children,
}: {
  /** A node so a label can carry a quiet qualifier ("Mobile — optional"). */
  label: ReactNode;
  audience: SignInControlAudience;
  /** Rendered at the right of the label row — e.g. "Forgot password?". */
  action?: ReactNode;
  /** Quiet guidance under the control, e.g. a minimum length. */
  hint?: ReactNode;
  children: ReactNode;
}) {
  const m = metrics(audience);
  return (
    <label className="flex flex-col" style={{ gap: m.labelGap }}>
      <span
        className="flex items-baseline justify-between gap-3"
        style={{ fontSize: m.labelSize, fontWeight: 600, color: SIGNIN_COLORS.label }}
      >
        <span>{label}</span>
        {action}
      </span>
      {children}
      {/* Margin rather than the flex `gap`, which is sized for the label-to-
          control step and is too generous between a control and its hint. */}
      {hint && (
        <span className="-mt-1" style={{ fontSize: 12.5, color: SIGNIN_COLORS.body }}>
          {hint}
        </span>
      )}
    </label>
  );
}

/**
 * The visitor canvas sits on paper like the desk audiences, so it takes their
 * neutral hairline, not the driver's sand one.
 */
function fieldBorder(audience: SignInControlAudience): string {
  return SIGNIN_THEME[audience === 'visitor' ? 'company' : audience].fieldBorder;
}

function inputStyle(audience: SignInControlAudience): CSSProperties {
  const m = metrics(audience);
  return {
    width: '100%',
    boxSizing: 'border-box',
    height: m.height,
    padding: `0 ${m.padX}px`,
    fontFamily: 'inherit',
    fontSize: m.fontSize,
    color: SIGNIN_COLORS.ink,
    background: SIGNIN_COLORS.fieldBg,
    // LONGHANDS, not `border: 1px solid …`. The focus state overrides only
    // `borderColor`, and on blur React REMOVES that key by setting
    // `border-color: ''` — which with a shorthand underneath wipes the colour
    // the shorthand set, so every field that had ever been focused fell back to
    // Tailwind preflight's #E5E7EB instead of the canvas hairline. Measured in
    // Chromium: #DDDACD → focus #244232 → blur #E5E7EB. React also logs a
    // "conflicting property" error for exactly this mix.
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: fieldBorder(audience),
    borderRadius: m.radius,
    transition: 'border-color .18s ease, box-shadow .18s ease',
  };
}

/**
 * Focus is applied through React state rather than a `:focus` rule because
 * these controls are styled inline — there is no stylesheet of ours to hang a
 * pseudo-class off, and the apps' global `:focus-visible` ring (index.css) is
 * keyboard-only and a different colour. A visible focus state on a login field
 * is not optional.
 */
function useFocusRing(audience: SignInControlAudience) {
  const [focused, setFocused] = useState(false);
  const focusStyle: CSSProperties = focused
    ? {
        outline: 'none',
        borderColor: SIGNIN_COLORS.focus,
        boxShadow: `0 0 0 3px ${SIGNIN_COLORS.focus}1F`,
      }
    : {};
  return {
    style: { ...inputStyle(audience), ...focusStyle },
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
  };
}

/**
 * iOS Safari zooms the viewport whenever a focused field's text is under 16px,
 * and the canvas puts the two DESK audiences at 15px. AdminLogin.tsx has
 * carried a `max-sm:text-base` override for exactly this reason since the
 * mobile pass; losing it here would reintroduce the zoom on every phone.
 *
 * `!` (important) because the size is an inline style on these controls, and an
 * ordinary class cannot beat one — an `!important` author declaration can. The
 * driver and visitor sizes are exempt: 17px is already clear of the floor.
 */
const DESK_PHONE_FONT = 'max-sm:!text-base';

export function SignInInput({
  audience,
  style,
  className = '',
  onFocus,
  onBlur,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { audience: SignInControlAudience }) {
  const ring = useFocusRing(audience);
  return (
    <input
      {...props}
      className={`${metrics(audience).fontSize >= 16 ? '' : DESK_PHONE_FONT} ${className}`}
      style={{ ...ring.style, ...style }}
      onFocus={(e) => {
        ring.onFocus();
        onFocus?.(e);
      }}
      onBlur={(e) => {
        ring.onBlur();
        onBlur?.(e);
      }}
    />
  );
}

/**
 * Password field with the canvas's mono SHOW/HIDE toggle.
 *
 * `onMouseDown` is prevented so clicking the toggle does not blur the input —
 * carried over from the shared PasswordInput, where it stops the field losing
 * the caret mid-typing. The toggle is a real <button> so it is keyboard
 * operable, and being interactive content it does not forward its click to the
 * <label>'s input the way a <span> would.
 */
export function SignInPasswordInput({
  audience,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { audience: SignInControlAudience }) {
  const [visible, setVisible] = useState(false);
  const m = metrics(audience);
  return (
    <span className="relative block">
      <SignInInput
        {...props}
        audience={audience}
        type={visible ? 'text' : 'password'}
        style={{ paddingRight: 78 }}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        onMouseDown={(e) => e.preventDefault()}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        className="absolute cursor-pointer rounded-lg border-none bg-transparent px-3"
        style={{
          right: 6,
          top: (m.height - 38) / 2,
          height: 38,
          fontFamily: SIGNIN_MONO,
          fontSize: 11,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          color: SIGNIN_COLORS.eyebrow,
        }}
      >
        {visible ? 'Hide' : 'Show'}
      </button>
    </span>
  );
}

/**
 * The submit button.
 *
 * The FILL IS THE TENANT'S, not the canvas's `#244232`/`#14120D`: this is the
 * one control on the page that carries brand, and `--brand-accent` is what
 * every other primary button in the product uses (buttonStyles.ts). A tenant
 * who set a purple brand gets a purple button here, exactly as they do inside
 * the app. Everything else about the control — height, radius, type scale — is
 * the canvas's.
 */
export function SignInButton({
  audience,
  loading = false,
  disabled,
  type,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  audience: SignInControlAudience;
  loading?: boolean;
  children: ReactNode;
}) {
  const m = metrics(audience);
  const off = disabled || loading;
  return (
    <button
      {...props}
      type={type ?? 'button'}
      disabled={off}
      className="inline-flex w-full items-center justify-center gap-2 border-none transition"
      style={{
        height: m.height,
        borderRadius: m.radius,
        background: 'var(--brand-accent)',
        color: SIGNIN_COLORS.paper,
        fontFamily: 'inherit',
        fontSize: m.buttonFontSize,
        fontWeight: 600,
        cursor: off ? 'not-allowed' : 'pointer',
        opacity: off ? 0.5 : 1,
      }}
    >
      {loading && (
        <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeOpacity=".25"
            strokeWidth="4"
          />
          <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        </svg>
      )}
      {children}
    </button>
  );
}

/** The page heading block: eyebrow, h1, lede. */
export function SignInHeading({
  eyebrow,
  title,
  children,
}: {
  eyebrow?: string;
  title: string;
  /** The lede paragraph. */
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-[11px]">
      {eyebrow && (
        <div
          style={{
            fontFamily: SIGNIN_MONO,
            fontSize: 11.5,
            letterSpacing: '1.7px',
            textTransform: 'uppercase',
            color: SIGNIN_COLORS.eyebrow,
          }}
        >
          {eyebrow}
        </div>
      )}
      <h1
        className="m-0 font-bold"
        style={{
          fontSize: 28,
          letterSpacing: '-0.8px',
          lineHeight: 1.18,
          color: SIGNIN_COLORS.ink,
          textWrap: 'pretty',
        }}
      >
        {title}
      </h1>
      {children && (
        <p
          className="m-0"
          style={{
            fontSize: 15,
            lineHeight: 1.6,
            color: SIGNIN_COLORS.body,
            textWrap: 'pretty',
          }}
        >
          {children}
        </p>
      )}
    </div>
  );
}

/** The green dotted note — the canvas's passwordless callout. */
export function SignInNote({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex items-start gap-[11px] rounded-[10px] px-[15px] py-3.5"
      style={{ background: SIGNIN_COLORS.noteBg, border: `1px solid ${SIGNIN_COLORS.noteBorder}` }}
    >
      <div
        className="mt-[7px] flex-none"
        style={{ width: 6, height: 6, borderRadius: 2, background: SIGNIN_COLORS.noteDot }}
        aria-hidden="true"
      />
      <div style={{ fontSize: 13.5, lineHeight: 1.55, color: SIGNIN_COLORS.noteText }}>
        {children}
      </div>
    </div>
  );
}

/** Hairline rule above the help block. */
export function SignInRule() {
  return <div style={{ height: 1, background: SIGNIN_COLORS.rule }} aria-hidden="true" />;
}

/**
 * The help text under the rule, including its link styling.
 *
 * The link rules skip `[data-product-link]` (the ProductLink credit in
 * PoweredByFooter), so "Powered by SupplierClear" keeps the look it has
 * everywhere else instead of turning green and semibold here.
 */
export function SignInHelp({ children }: { children: ReactNode }) {
  return (
    <div
      className="flex flex-col gap-[9px] [&_a:not([data-product-link]):hover]:text-[#14120D] [&_a:not([data-product-link])]:font-semibold [&_a:not([data-product-link])]:text-[#244232] [&_a:not([data-product-link])]:underline [&_a:not([data-product-link])]:decoration-1 [&_a:not([data-product-link])]:underline-offset-[3px]"
      style={{ fontSize: 13.5, lineHeight: 1.55, color: SIGNIN_COLORS.body }}
    >
      {children}
    </div>
  );
}

/** Mono uppercase text button — "← Change email". */
export function SignInBackButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="cursor-pointer self-start border-none bg-transparent p-0 hover:text-[#244232]"
      style={{
        fontFamily: SIGNIN_MONO,
        fontSize: 11.5,
        letterSpacing: '1.4px',
        textTransform: 'uppercase',
        color: SIGNIN_COLORS.eyebrow,
      }}
    >
      {children}
    </button>
  );
}
